/**
 * Reading the Patient Access call notes as one family.
 *
 * A lead's intake is charted on one of two cards — the PAC intro call or the PN
 * consultation. Both ride on the `ci-intro-call-note` category and the
 * `ci-intro-call-answers` payload, told apart by visit type, and the answers the
 * patient list needs — market, payment method, outcome — sit under the same ids
 * on both. These helpers let a list read "the intake note" without caring which
 * card it was, and keep that read in one place for the day the PN consultation
 * takes a category of its own.
 */

import { Communication } from '@medplum/fhirtypes';
import { PatientAccessCallAnswers, isCiIntroCallNote, parseCiIntroCallAnswers } from './patient-access-note';
import { getPnConsultationOutcome } from './pn-consultation-note';
import { readPaymentTypes } from './pn-consultation-v2-script';

export { PAC_CALL_NOTE_CATEGORIES } from './constants';

/** The beat id both scripts record the payment method under. */
const PAYMENT_ANSWER_ID = 'pay';

/**
 * Parses whichever card this is. A note of neither category yields empty
 * answers rather than throwing — the list degrades a cell to an em-dash, it does
 * not lose the row.
 */
export function parsePatientAccessCallAnswers(
  note: Pick<Communication, 'category' | 'payload'>
): PatientAccessCallAnswers {
  if (isCiIntroCallNote(note)) {
    return parseCiIntroCallAnswers(note);
  }
  return { values: {} };
}

/** The note charted against one appointment, by `basedOn`. */
export function noteForAppointment(
  notes: Communication[] | undefined,
  appointmentReference: string | undefined
): Communication | undefined {
  if (!appointmentReference) {
    return undefined;
  }
  return notes?.find((note) => note.basedOn?.some((reference) => reference.reference === appointmentReference));
}

export function getPaymentMethod(answers: PatientAccessCallAnswers | undefined): string | undefined {
  // The PN card v2.0 records several payment types (BAR-3000); they are the
  // newer answer when a note carries both.
  const types = answers ? readPaymentTypes(answers.values) : [];
  if (types.length > 0) return types.join(', ');
  const value = answers?.values[PAYMENT_ANSWER_ID];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The PN consultation's "next step" is its Outcome select — the card has no
 * separate next-step field because the next step out of a PN consult is payment.
 * Undefined until the navigator has saved an outcome.
 */
export function getPnConsultNextStep(note: Communication | undefined): string | undefined {
  return note ? getPnConsultationOutcome(parseCiIntroCallAnswers(note)) : undefined;
}
